import { useEffect, useState } from 'react'
import { CircleHelp, Settings2Icon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { RaidPlanEncounterAssignmentAbility } from 'app/frontend/types/custom/RaidPlan'
import {
  normalizeCustomGrouping,
  sanitizeCustomGroupingInput,
} from './assignmentTimeline'

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  ability: RaidPlanEncounterAssignmentAbility
  spellName: string
  canEdit: boolean
  assignmentGroups: number
  onUpdate: (
    updater: (ability: RaidPlanEncounterAssignmentAbility) => void,
    pushHistory?: boolean,
  ) => void
  onCommit: () => void
}

const TeamPlanAssignmentSettingsPopover = ({
  open,
  onOpenChange,
  ability,
  spellName,
  canEdit,
  assignmentGroups,
  onUpdate,
  onCommit,
}: Props) => {
  const { t } = useTranslation()
  const [customGroupingDraft, setCustomGroupingDraft] = useState(
    ability.custom_grouping ?? '',
  )
  const [isEditingCustomGrouping, setIsEditingCustomGrouping] = useState(false)
  const [assignmentGroupsDraft, setAssignmentGroupsDraft] = useState(
    assignmentGroups.toString(),
  )
  const [isEditingAssignmentGroups, setIsEditingAssignmentGroups] =
    useState(false)

  const sanitizeAssignmentGroupsInput = (value: string) =>
    value.replace(/[^\d]/g, '')

  const normalizeAssignmentGroups = (value: string) => {
    const sanitized = sanitizeAssignmentGroupsInput(value)
    if (!sanitized) return undefined
    const parsed = Number(sanitized)
    if (!Number.isInteger(parsed)) return undefined
    return Math.min(Math.max(parsed, 1), 20)
  }

  useEffect(() => {
    if (isEditingCustomGrouping) return
    setCustomGroupingDraft(ability.custom_grouping ?? '')
  }, [ability.custom_grouping, isEditingCustomGrouping, open])

  useEffect(() => {
    if (isEditingAssignmentGroups) return
    setAssignmentGroupsDraft(assignmentGroups.toString())
  }, [assignmentGroups, isEditingAssignmentGroups, open])

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button type='button' variant='outline'>
          <Settings2Icon size={16} />
        </Button>
      </PopoverTrigger>
      <PopoverContent className='w-72 space-y-2'>
        <div className='space-y-1'>
          <div className='flex items-end gap-2'>
            <div className='space-y-1 flex-1'>
              <div className='flex items-center gap-1'>
                <span className='text-xs text-muted-foreground'>
                  {t('dashboard.team.plans.assignments.custom-grouping')}
                </span>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className='inline-flex items-center text-muted-foreground'>
                      <CircleHelp className='h-3.5 w-3.5' />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent className='max-w-72 text-xs'>
                    {t(
                      'dashboard.team.plans.assignments.custom-grouping-tooltip',
                    )}
                  </TooltipContent>
                </Tooltip>
              </div>
              <Input
                value={customGroupingDraft}
                onFocus={() => setIsEditingCustomGrouping(true)}
                onChange={(event) => {
                  const nextValue = event.target.value
                  setCustomGroupingDraft(nextValue)
                  const sanitized = sanitizeCustomGroupingInput(nextValue)
                  const normalized = normalizeCustomGrouping(sanitized)
                  if (normalized || sanitized.trim() === '') {
                    onUpdate((nextAbility) => {
                      nextAbility.custom_grouping = normalized
                    }, false)
                  }
                }}
                onBlur={() => {
                  const sanitized =
                    sanitizeCustomGroupingInput(customGroupingDraft)
                  const normalized = normalizeCustomGrouping(sanitized)
                  setIsEditingCustomGrouping(false)
                  setCustomGroupingDraft(normalized ?? '')
                  onUpdate((nextAbility) => {
                    nextAbility.custom_grouping = normalized
                  }, false)
                  onCommit()
                }}
                placeholder={t(
                  'dashboard.team.plans.assignments.custom-grouping-placeholder',
                )}
                className='h-8 text-xs'
                disabled={!canEdit}
              />
            </div>
            <div className='space-y-1 w-28'>
              <span className='text-xs text-muted-foreground'>
                {t('dashboard.team.plans.assignments.groups-per-cast')}
              </span>
              <Input
                type='text'
                inputMode='numeric'
                maxLength={2}
                value={assignmentGroupsDraft}
                onFocus={() => setIsEditingAssignmentGroups(true)}
                onChange={(event) => {
                  const nextValue = event.target.value
                  setAssignmentGroupsDraft(nextValue)
                  const sanitized = sanitizeAssignmentGroupsInput(nextValue)
                  const normalized = normalizeAssignmentGroups(nextValue)
                  if (normalized || sanitized.trim() === '') {
                    onUpdate((nextAbility) => {
                      nextAbility.assignment_groups = normalized
                    }, false)
                  }
                }}
                onBlur={() => {
                  setIsEditingAssignmentGroups(false)
                  const normalized = normalizeAssignmentGroups(
                    assignmentGroupsDraft,
                  )
                  setAssignmentGroupsDraft(normalized?.toString() ?? '')
                  onUpdate((nextAbility) => {
                    nextAbility.assignment_groups = normalized
                  }, false)
                  onCommit()
                }}
                className='h-8 text-xs'
                disabled={!canEdit}
              />
            </div>
          </div>
        </div>
        <span className='text-xs text-muted-foreground'>
          {t('dashboard.team.plans.assignments.note-options')}
        </span>
        <Input
          value={ability.message ?? ''}
          onChange={(event) =>
            onUpdate((nextAbility) => {
              nextAbility.message = event.target.value || undefined
            }, false)
          }
          onBlur={onCommit}
          placeholder={`Soak ${spellName}`}
          className='h-8 text-xs'
          disabled={!canEdit}
        />
        <div className='flex items-center gap-2'>
          <Input
            type='number'
            step='0.1'
            value={ability.offset_seconds ?? ''}
            placeholder={'0'}
            onChange={(event) => {
              const value = event.target.value
              const parsed = Number(value)
              onUpdate((nextAbility) => {
                nextAbility.offset_seconds =
                  value === '' || !Number.isFinite(parsed) ? undefined : parsed
              }, false)
            }}
            onBlur={onCommit}
            className='h-8 text-xs'
            disabled={!canEdit}
          />
          <span className='text-xs text-muted-foreground whitespace-nowrap'>
            {t('dashboard.team.plans.assignments.offset-seconds')}
          </span>
        </div>
      </PopoverContent>
    </Popover>
  )
}

export default TeamPlanAssignmentSettingsPopover
